import type { OfferingCatalogTree, SaveOfferingCatalogInput } from '@repo/types'
import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { CardContent } from '@repo/ui/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/ui/select'
import { Switch } from '@repo/ui/components/ui/switch'
import { Textarea } from '@repo/ui/components/ui/textarea'
import { surfaceSpacing } from '@repo/ui/lib/design-tokens'
import { cn } from '@repo/ui/lib/utils'
import { ChevronDown, ChevronUp, Copy, Image, Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AppTopBarActionGroup, AppTopBarActions } from '@/components/app/top-bar-actions'
import {
  AppWorkspaceMainSupportingSplit,
  AppWorkspacePane,
  AppWorkspaceTabbedPanel,
  WorkspaceInspectorPane,
  WorkspaceMetricStrip,
  WorkspaceNotice,
  WorkspaceSectionCard,
  WorkspaceStatusRow,
} from '@/components/app/workspace-shell'
import { WorkspaceEmptyState } from '@/components/operator/workspace-empty-state'
import { PremiumPanel } from '@/components/ui/premium-panel'
import {
  type CatalogBookingPageConfig,
  DEFAULT_CATALOG_BOOKING_CONFIG,
  DEFAULT_DISPLAY_GROUP_BOOKING_CONFIG,
  DEFAULT_SERVICE_BOOKING_CONFIG,
  type DisplayGroupBookingPageConfig,
  formatBookingPrice,
  resolveServicePriceDisplay,
  type ServiceBookingPageConfig,
} from '@/lib/booking-page-customization'
import { createTrpcClient } from '@/lib/config/trpc-client'
import { uploadFile } from '@/lib/services/upload'
import { trpc } from '@/lib/trpc'

type DraftItem = {
  id?: string
  name: string
  slug: string
  description: string
  durationMinutes: number
  price: number | null
  allowQuantity: boolean
  quantityLabel: string
  maxQuantity: number
  durationPerAdditionalUnit: number
  pricePerAdditionalUnit: number
  bufferMode: 'use_menu_default' | 'custom'
  bufferMinutesBefore: number
  bufferMinutesAfter: number
  minimumNoticeMinutes: number
  bookingPageConfig: ServiceBookingPageConfig
  isActive: boolean
}

type DraftCategory = {
  id?: string
  name: string
  isActive: boolean
  bookingPageConfig: DisplayGroupBookingPageConfig
  items: DraftItem[]
}

type DraftMenu = {
  id?: string
  name: string
  slug: string
  isActive: boolean
  bufferMinutesBeforeDefault: number
  bufferMinutesAfterDefault: number
  minimumNoticeMinutesDefault: number
  bookingPageConfig: CatalogBookingPageConfig
  categories: DraftCategory[]
}

type PendingServiceImages = Record<string, { file: File; previewUrl: string }>

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function parseCurrencyToCents(value: string): number | null {
  const normalized = value.trim()
  if (!normalized) return null
  const numeric = Number(normalized)
  if (!Number.isFinite(numeric)) return null
  return Math.max(0, Math.round(numeric * 100))
}

function formatCents(value: number | null | undefined): string {
  if (value == null) return ''
  return (value / 100).toFixed(2)
}

function nextSlug(base: string, suffix: string) {
  const slug = slugify(`${base}-${suffix}`)
  return slug || `menu-${Date.now()}`
}

function toDraftMenu(data: OfferingCatalogTree): DraftMenu {
  return {
    id: data.catalog.id,
    name: data.catalog.name,
    slug: data.catalog.slug,
    isActive: data.catalog.isActive,
    bufferMinutesBeforeDefault: data.catalog.bufferMinutesBeforeDefault,
    bufferMinutesAfterDefault: data.catalog.bufferMinutesAfterDefault,
    minimumNoticeMinutesDefault: data.catalog.minimumNoticeMinutesDefault,
    bookingPageConfig: data.catalog.bookingPageConfig ?? DEFAULT_CATALOG_BOOKING_CONFIG,
    categories: data.displayGroups
      .slice()
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map((category) => ({
        id: category.id,
        name: category.name,
        isActive: category.isActive,
        bookingPageConfig: category.bookingPageConfig ?? DEFAULT_DISPLAY_GROUP_BOOKING_CONFIG,
        items: category.offerings
          .slice()
          .sort((left, right) => left.sortOrder - right.sortOrder)
          .map((item) => ({
            id: item.id,
            name: item.name,
            slug: item.slug,
            description: item.description ?? '',
            durationMinutes: item.durationMinutes,
            price: item.price,
            allowQuantity: item.allowQuantity,
            quantityLabel: item.quantityLabel ?? 'Quantity',
            maxQuantity: item.maxQuantity ?? 1,
            durationPerAdditionalUnit: item.durationPerAdditionalUnit ?? 0,
            pricePerAdditionalUnit: item.pricePerAdditionalUnit ?? 0,
            bufferMode: item.bufferMode === 'custom' ? 'custom' : 'use_menu_default',
            bufferMinutesBefore: item.bufferMinutesBefore,
            bufferMinutesAfter: item.bufferMinutesAfter,
            minimumNoticeMinutes: item.minimumNoticeMinutes,
            bookingPageConfig: item.bookingPageConfig ?? DEFAULT_SERVICE_BOOKING_CONFIG,
            isActive: item.isActive,
          })),
      })),
  }
}

function createDefaultItem(): DraftItem {
  return {
    name: '',
    slug: '',
    description: '',
    durationMinutes: 60,
    price: null,
    allowQuantity: false,
    quantityLabel: 'Quantity',
    maxQuantity: 1,
    durationPerAdditionalUnit: 0,
    pricePerAdditionalUnit: 0,
    bufferMode: 'use_menu_default',
    bufferMinutesBefore: 0,
    bufferMinutesAfter: 0,
    minimumNoticeMinutes: 60,
    bookingPageConfig: DEFAULT_SERVICE_BOOKING_CONFIG,
    isActive: true,
  }
}

function createDefaultCategory(index: number): DraftCategory {
  return {
    name: `Category ${index + 1}`,
    isActive: true,
    bookingPageConfig: DEFAULT_DISPLAY_GROUP_BOOKING_CONFIG,
    items: [],
  }
}

function normalizeDraft(menu: DraftMenu): DraftMenu {
  return {
    ...menu,
    categories: menu.categories.map((category) => ({
      ...category,
      items: category.items.map((item) => ({ ...item })),
    })),
  }
}

type PricingDisplayValue = ServiceBookingPageConfig['priceDisplay']

function BadgeLabelEditor({
  id,
  value,
  onChange,
}: {
  id: string
  value: string
  onChange: (value: string) => void
}) {
  const preview = value.trim()

  return (
    <div className="rounded-[6px] border border-border/70 bg-muted/15 p-3">
      <div className="flex min-h-6 items-center justify-between gap-3">
        <Label htmlFor={id}>Booking badge</Label>
        {preview ? (
          <Badge variant="secondary" className="max-w-36 truncate">
            {preview}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">No badge</span>
        )}
      </div>
      <div className="mt-2 flex gap-2">
        <Input
          id={id}
          value={value}
          maxLength={32}
          className="h-9 bg-background"
          onChange={(event) => onChange(event.target.value.slice(0, 32))}
          placeholder="Most booked"
        />
        {preview ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 cursor-pointer px-3"
            onClick={() => onChange('')}
          >
            Clear
          </Button>
        ) : null}
      </div>
    </div>
  )
}

function PrepChecklistEditor({
  id,
  items,
  onChange,
  label = 'Prep checklist',
  emptyLabel = 'No prep items yet.',
  placeholder = 'Add a prep item',
}: {
  id: string
  items: string[]
  onChange: (items: string[]) => void
  label?: string
  emptyLabel?: string
  placeholder?: string
}) {
  const [newItem, setNewItem] = useState('')
  const canAdd = newItem.trim().length > 0 && items.length < 8

  function updateChecklistItem(index: number, value: string) {
    onChange(items.map((item, itemIndex) => (itemIndex === index ? value.slice(0, 120) : item)))
  }

  function removeChecklistItem(index: number) {
    onChange(items.filter((_, itemIndex) => itemIndex !== index))
  }

  function compactChecklist() {
    onChange(
      items
        .map((item) => item.trim())
        .filter(Boolean)
        .slice(0, 8)
    )
  }

  function addChecklistItem() {
    const nextItem = newItem.trim()
    if (!nextItem || items.length >= 8) return

    onChange([...items, nextItem.slice(0, 120)])
    setNewItem('')
  }

  return (
    <div className="space-y-3 rounded-[6px] border border-border/70 bg-muted/15 p-3">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={`${id}-new`}>{label}</Label>
        <span className="text-xs text-muted-foreground">{items.length}/8</span>
      </div>
      {items.length > 0 ? (
        <div className="space-y-2">
          {items.map((item, index) => (
            <div
              key={`${id}-item-${index}`}
              className="grid grid-cols-[1.5rem_minmax(0,1fr)_2.25rem] items-center gap-2"
            >
              <span className="text-right text-xs font-semibold text-muted-foreground">
                {index + 1}
              </span>
              <Input
                value={item}
                maxLength={120}
                className="h-9 bg-background"
                onChange={(event) => updateChecklistItem(index, event.target.value)}
                onBlur={compactChecklist}
                aria-label={`Prep checklist item ${index + 1}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 cursor-pointer"
                onClick={() => removeChecklistItem(index)}
                aria-label={`Remove prep checklist item ${index + 1}`}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-[4px] border border-dashed border-border/70 bg-background/65 px-3 py-2 text-sm text-muted-foreground">
          {emptyLabel}
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
        <Input
          id={`${id}-new`}
          value={newItem}
          maxLength={120}
          className="h-9 bg-background"
          onChange={(event) => setNewItem(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              addChecklistItem()
            }
          }}
          placeholder={placeholder}
        />
        <Button
          type="button"
          variant="outline"
          className="h-9 cursor-pointer"
          disabled={!canAdd}
          onClick={addChecklistItem}
        >
          <Plus className="h-4 w-4" />
          Add item
        </Button>
      </div>
    </div>
  )
}

function PricingDisplaySelect({
  id,
  value,
  onChange,
  includeInherit = false,
}: {
  id: string
  value: PricingDisplayValue
  onChange: (value: PricingDisplayValue) => void
  includeInherit?: boolean
}) {
  const options = [
    ...(includeInherit ? [{ value: 'inherit', label: 'Use group setting' }] : []),
    { value: 'standard', label: 'Show exact price' },
    { value: 'from', label: 'Show from price' },
    { value: 'hidden', label: 'Hide price' },
  ] as const

  return (
    <Select value={value} onValueChange={(nextValue: PricingDisplayValue) => onChange(nextValue)}>
      <SelectTrigger id={id} className="h-10 bg-background">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function ServicesWorkspace() {
  const utils = trpc.useUtils()
  const trpcClient = useMemo(() => createTrpcClient(), [])
  const menusQuery = trpc.services.catalogs.list.useQuery()
  const saveMutation = trpc.services.catalogs.save.useMutation()
  const activateMutation = trpc.services.catalogs.activate.useMutation()
  const deleteMutation = trpc.services.catalogs.delete.useMutation()

  const [selectedMenuId, setSelectedMenuId] = useState('')
  const [draft, setDraft] = useState<DraftMenu | null>(null)
  const [savedSnapshot, setSavedSnapshot] = useState<DraftMenu | null>(null)
  const [expandedOfferingKeys, setExpandedOfferingKeys] = useState<Set<string>>(() => new Set())
  const [pendingServiceImages, setPendingServiceImages] = useState<PendingServiceImages>({})

  useEffect(() => {
    if (selectedMenuId || !menusQuery.data || menusQuery.data.length === 0) return

    setSelectedMenuId(menusQuery.data[0].id)
  }, [menusQuery.data, selectedMenuId])

  const selectedSummary = useMemo(
    () => menusQuery.data?.find((menu) => menu.id === selectedMenuId) ?? null,
    [menusQuery.data, selectedMenuId]
  )
  const menuQuery = trpc.services.catalogs.get.useQuery(
    { catalogId: selectedMenuId },
    { enabled: Boolean(selectedMenuId) }
  )

  useEffect(() => {
    if (menuQuery.data) {
      const nextDraft = toDraftMenu(menuQuery.data)
      setDraft(nextDraft)
      setSavedSnapshot(normalizeDraft(nextDraft))
      setExpandedOfferingKeys(new Set())
      setPendingServiceImages((current) => {
        Object.values(current).forEach((pending) => {
          URL.revokeObjectURL(pending.previewUrl)
        })
        return {}
      })
    }
  }, [menuQuery.data])

  async function refreshMenus() {
    await menusQuery.refetch()
  }

  async function handleCreateMenu() {
    const count = (menusQuery.data?.length ?? 0) + 1
    const menuName = `Catalog ${count}`
    const created = await saveMutation.mutateAsync({
      catalog: {
        name: menuName,
        slug: nextSlug('catalog', String(count)),
        isActive: (menusQuery.data?.length ?? 0) === 0,
        bufferMinutesBeforeDefault: 0,
        bufferMinutesAfterDefault: 0,
        minimumNoticeMinutesDefault: 60,
        bookingPageConfig: DEFAULT_CATALOG_BOOKING_CONFIG,
        displayGroups: [
          {
            name: 'General',
            isActive: true,
            bookingPageConfig: DEFAULT_DISPLAY_GROUP_BOOKING_CONFIG,
            offerings: [],
          },
        ],
      },
    })

    await refreshMenus()
    setSelectedMenuId(created.catalog.id)
    const nextDraft = toDraftMenu(created)
    setDraft(nextDraft)
    setSavedSnapshot(normalizeDraft(nextDraft))
    setExpandedOfferingKeys(new Set())
    toast.success('Catalog saved.')
  }

  async function handleDuplicateMenu(sourceCatalogId: string) {
    const sourceTree =
      sourceCatalogId === selectedMenuId && draft
        ? ({
            catalog: {
              id: draft.id ?? sourceCatalogId,
              name: draft.name,
              slug: draft.slug,
              isActive: draft.isActive,
              bufferMinutesBeforeDefault: draft.bufferMinutesBeforeDefault,
              bufferMinutesAfterDefault: draft.bufferMinutesAfterDefault,
              minimumNoticeMinutesDefault: draft.minimumNoticeMinutesDefault,
              bookingPageConfig: draft.bookingPageConfig,
            },
            displayGroups: draft.categories.map((category, categoryIndex) => ({
              id: category.id ?? `draft-category-${categoryIndex}`,
              name: category.name,
              isActive: category.isActive,
              bookingPageConfig: category.bookingPageConfig,
              sortOrder: categoryIndex,
              offerings: category.items.map((item, itemIndex) => ({
                ...item,
                id: item.id ?? `draft-item-${itemIndex}`,
                sortOrder: itemIndex,
                bufferMode: item.bufferMode === 'custom' ? 'custom' : 'use_catalog_default',
              })),
            })),
          } as OfferingCatalogTree)
        : await utils.services.catalogs.get.fetch({ catalogId: sourceCatalogId })

    const sourceDraft = toDraftMenu(sourceTree)
    const copyName = `${sourceDraft.name} Copy`
    const duplicated = await saveMutation.mutateAsync({
      catalog: {
        id: undefined,
        name: copyName,
        slug: nextSlug(sourceDraft.slug || 'catalog', 'copy'),
        isActive: false,
        bufferMinutesBeforeDefault: sourceDraft.bufferMinutesBeforeDefault,
        bufferMinutesAfterDefault: sourceDraft.bufferMinutesAfterDefault,
        minimumNoticeMinutesDefault: sourceDraft.minimumNoticeMinutesDefault,
        bookingPageConfig: sourceDraft.bookingPageConfig,
        displayGroups: sourceDraft.categories.map((category) => ({
          id: undefined,
          name: category.name,
          isActive: category.isActive,
          bookingPageConfig: category.bookingPageConfig,
          offerings: category.items.map((item) => ({
            ...item,
            id: undefined,
            bufferMode: item.bufferMode === 'custom' ? 'custom' : 'use_catalog_default',
          })),
        })),
      },
    })

    await refreshMenus()
    setSelectedMenuId(duplicated.catalog.id)
    const nextDraft = toDraftMenu(duplicated)
    setDraft(nextDraft)
    setSavedSnapshot(normalizeDraft(nextDraft))
    setExpandedOfferingKeys(new Set())
  }

  async function handleDeleteMenu() {
    if (!selectedMenuId) return

    await deleteMutation.mutateAsync({ catalogId: selectedMenuId })
    await refreshMenus()

    const nextMenus = await menusQuery.refetch()
    const nextId = nextMenus.data?.[0]?.id ?? ''
    setSelectedMenuId(nextId)
    setDraft(null)
    setSavedSnapshot(null)
    setExpandedOfferingKeys(new Set())
  }

  async function handleActivateMenu() {
    if (!selectedMenuId) return

    await activateMutation.mutateAsync({ catalogId: selectedMenuId })
    await refreshMenus()
  }

  async function handleSaveMenu() {
    if (!draft) return
    const normalized = normalizeDraft(draft)
    const displayGroups: Array<
      Omit<
        NonNullable<SaveOfferingCatalogInput['catalog']['displayGroups']>[number],
        'offerings'
      > & {
        offerings: NonNullable<
          NonNullable<SaveOfferingCatalogInput['catalog']['displayGroups']>[number]['offerings']
        >
      }
    > = []

    for (const [categoryIndex, category] of normalized.categories.entries()) {
      const offerings: NonNullable<
        NonNullable<SaveOfferingCatalogInput['catalog']['displayGroups']>[number]['offerings']
      > = []

      for (const [itemIndex, item] of category.items.entries()) {
        const imageKey = `${categoryIndex}-${itemIndex}`
        const pendingImage = pendingServiceImages[imageKey]
        const imageUrl = pendingImage
          ? await uploadFile(pendingImage.file, trpcClient, {
              pathPrefix: 'service-images',
              maxSizeBytes: 5 * 1024 * 1024,
            })
          : item.bookingPageConfig.imageUrl.trim()
        const bufferMode: 'custom' | 'use_catalog_default' =
          item.bufferMode === 'custom' ? 'custom' : 'use_catalog_default'

        offerings.push({
          ...item,
          slug: slugify(item.slug || item.name),
          quantityLabel: item.allowQuantity ? item.quantityLabel : undefined,
          maxQuantity: item.allowQuantity ? item.maxQuantity : 1,
          durationPerAdditionalUnit: item.allowQuantity ? item.durationPerAdditionalUnit : 0,
          pricePerAdditionalUnit: item.allowQuantity ? item.pricePerAdditionalUnit : 0,
          bufferMode,
          bufferMinutesBefore: item.bufferMode === 'custom' ? item.bufferMinutesBefore : undefined,
          bufferMinutesAfter: item.bufferMode === 'custom' ? item.bufferMinutesAfter : undefined,
          minimumNoticeMinutes: item.minimumNoticeMinutes,
          bookingPageConfig: {
            ...item.bookingPageConfig,
            prepChecklist: item.bookingPageConfig.prepChecklist
              .map((entry) => entry.trim())
              .filter(Boolean)
              .slice(0, 8),
            includedItems: item.bookingPageConfig.includedItems
              .map((entry) => entry.trim())
              .filter(Boolean)
              .slice(0, 8),
            featuredLabel: item.bookingPageConfig.featuredLabel.trim(),
            imageUrl,
            upsellNote: item.bookingPageConfig.upsellNote.trim(),
          },
        })
      }

      displayGroups.push({
        id: category.id,
        name: category.name,
        isActive: category.isActive,
        bookingPageConfig: category.bookingPageConfig,
        offerings,
      })
    }

    const saved = await saveMutation.mutateAsync({
      catalog: {
        id: normalized.id,
        name: normalized.name,
        slug: normalized.slug,
        isActive: normalized.isActive,
        bufferMinutesBeforeDefault: normalized.bufferMinutesBeforeDefault,
        bufferMinutesAfterDefault: normalized.bufferMinutesAfterDefault,
        minimumNoticeMinutesDefault: normalized.minimumNoticeMinutesDefault,
        bookingPageConfig: normalized.bookingPageConfig,
        displayGroups,
      },
    })

    await refreshMenus()
    setSelectedMenuId(saved.catalog.id)
    const nextDraft = toDraftMenu(saved)
    setDraft(nextDraft)
    setSavedSnapshot(normalizeDraft(nextDraft))
    setExpandedOfferingKeys(new Set())
    setPendingServiceImages((current) => {
      Object.values(current).forEach((pending) => {
        URL.revokeObjectURL(pending.previewUrl)
      })
      return {}
    })
  }

  function updateDraft(patch: Partial<DraftMenu>) {
    setDraft((current) => (current ? { ...current, ...patch } : current))
  }

  function updateCategory(categoryIndex: number, patch: Partial<DraftCategory>) {
    setDraft((current) => {
      if (!current) return current
      return {
        ...current,
        categories: current.categories.map((category, index) =>
          index === categoryIndex ? { ...category, ...patch } : category
        ),
      }
    })
  }

  function updateItem(categoryIndex: number, itemIndex: number, patch: Partial<DraftItem>) {
    setDraft((current) => {
      if (!current) return current
      return {
        ...current,
        categories: current.categories.map((category, cIndex) => {
          if (cIndex !== categoryIndex) return category
          return {
            ...category,
            items: category.items.map((item, iIndex) =>
              iIndex === itemIndex ? { ...item, ...patch } : item
            ),
          }
        }),
      }
    })
  }

  function previewServiceImage(categoryIndex: number, itemIndex: number, file: File | undefined) {
    if (!file) return

    const imageKey = `${categoryIndex}-${itemIndex}`
    const previewUrl = URL.createObjectURL(file)
    setPendingServiceImages((current) => {
      const previous = current[imageKey]
      if (previous) URL.revokeObjectURL(previous.previewUrl)
      return { ...current, [imageKey]: { file, previewUrl } }
    })
    const item = draft?.categories[categoryIndex]?.items[itemIndex]
    if (!item) return

    updateItem(categoryIndex, itemIndex, {
      bookingPageConfig: {
        ...item.bookingPageConfig,
        imageUrl: previewUrl,
      },
    })
  }

  function addCategory() {
    setDraft((current) => {
      if (!current) return current
      return {
        ...current,
        categories: [...current.categories, createDefaultCategory(current.categories.length)],
      }
    })
  }

  function removeCategory(categoryIndex: number) {
    setDraft((current) => {
      if (!current) return current
      return {
        ...current,
        categories: current.categories.filter((_, index) => index !== categoryIndex),
      }
    })
  }

  function addItem(categoryIndex: number) {
    const nextItemIndex = draft?.categories[categoryIndex]?.items.length ?? 0
    const nextKey = getOfferingKey(categoryIndex, nextItemIndex)
    setExpandedOfferingKeys((current) => new Set(current).add(nextKey))
    setDraft((current) => {
      if (!current) return current
      return {
        ...current,
        categories: current.categories.map((category, index) =>
          index === categoryIndex
            ? { ...category, items: [...category.items, createDefaultItem()] }
            : category
        ),
      }
    })
  }

  function removeItem(categoryIndex: number, itemIndex: number) {
    const offeringKey = getOfferingKey(categoryIndex, itemIndex)
    setExpandedOfferingKeys((current) => {
      const next = new Set(current)
      next.delete(offeringKey)
      return next
    })
    setDraft((current) => {
      if (!current) return current
      return {
        ...current,
        categories: current.categories.map((category, index) =>
          index === categoryIndex
            ? { ...category, items: category.items.filter((_, i) => i !== itemIndex) }
            : category
        ),
      }
    })
  }

  function moveCategory(categoryIndex: number, direction: 'up' | 'down') {
    setDraft((current) => {
      if (!current) return current
      const nextIndex = direction === 'up' ? categoryIndex - 1 : categoryIndex + 1
      if (nextIndex < 0 || nextIndex >= current.categories.length) return current

      const categories = [...current.categories]
      ;[categories[categoryIndex], categories[nextIndex]] = [
        categories[nextIndex],
        categories[categoryIndex],
      ]

      return { ...current, categories }
    })
  }

  function moveItem(categoryIndex: number, itemIndex: number, direction: 'up' | 'down') {
    setDraft((current) => {
      if (!current) return current
      const categories = [...current.categories]
      const category = categories[categoryIndex]
      if (!category) return current

      const nextIndex = direction === 'up' ? itemIndex - 1 : itemIndex + 1
      if (nextIndex < 0 || nextIndex >= category.items.length) return current

      const items = [...category.items]
      ;[items[itemIndex], items[nextIndex]] = [items[nextIndex], items[itemIndex]]
      categories[categoryIndex] = { ...category, items }

      return { ...current, categories }
    })
  }

  function getOfferingKey(categoryIndex: number, itemIndex: number) {
    return `${categoryIndex}:${itemIndex}`
  }

  function toggleOfferingRow(categoryIndex: number, itemIndex: number) {
    const offeringKey = getOfferingKey(categoryIndex, itemIndex)
    setExpandedOfferingKeys((current) => {
      const next = new Set(current)
      if (next.has(offeringKey)) {
        next.delete(offeringKey)
      } else {
        next.add(offeringKey)
      }
      return next
    })
  }

  function expandOfferingRow(categoryIndex: number, itemIndex: number) {
    const offeringKey = getOfferingKey(categoryIndex, itemIndex)
    setExpandedOfferingKeys((current) => new Set(current).add(offeringKey))
  }

  const busy =
    saveMutation.isPending ||
    activateMutation.isPending ||
    deleteMutation.isPending ||
    menusQuery.isFetching

  const menuCount = menusQuery.data?.length ?? 0
  const hasMenus = menuCount > 0
  const isDirty =
    draft != null &&
    savedSnapshot != null &&
    JSON.stringify(normalizeDraft(draft)) !== JSON.stringify(savedSnapshot)
  const draftStats = useMemo(() => {
    if (!draft) {
      return {
        categoryCount: 0,
        itemCount: 0,
        activeItemCount: 0,
        pricedItemCount: 0,
        averageDuration: 0,
      }
    }

    const items = draft.categories.flatMap((category) => category.items)
    const itemCount = items.length
    const activeItemCount = items.filter((item) => item.isActive).length
    const pricedItemCount = items.filter((item) => item.price != null).length
    const averageDuration =
      itemCount > 0
        ? Math.round(items.reduce((total, item) => total + item.durationMinutes, 0) / itemCount)
        : 0

    return {
      categoryCount: draft.categories.length,
      itemCount,
      activeItemCount,
      pricedItemCount,
      averageDuration,
    }
  }, [draft])
  const isSelectedMenuLoading = hasMenus && Boolean(selectedMenuId) && menuQuery.isLoading && !draft
  const selectedMenuError = hasMenus && Boolean(selectedMenuId) ? menuQuery.error : null
  const previewCategoryGroups = useMemo(() => {
    if (!draft) return []

    return draft.categories.map((category) => ({
      name: category.name,
      items: category.items.map((item) => ({
        name: item.name || 'Untitled offering',
        price: formatBookingPrice(
          item.price,
          resolveServicePriceDisplay(item.bookingPageConfig, category.bookingPageConfig)
        ),
        duration: `${item.durationMinutes} min`,
      })),
    }))
  }, [draft])
  if (menusQuery.isLoading && !menusQuery.data) {
    return (
      <PremiumPanel>
        <CardContent className={`${surfaceSpacing.insetY.roomy} text-sm text-muted-foreground`}>
          Loading services workspace…
        </CardContent>
      </PremiumPanel>
    )
  }

  if (menusQuery.error) {
    return (
      <PremiumPanel tone="destructive">
        <CardContent className={`${surfaceSpacing.insetY.roomy} text-sm text-destructive`}>
          {menusQuery.error.message}
        </CardContent>
      </PremiumPanel>
    )
  }

  return (
    <div className="space-y-6">
      <AppTopBarActions>
        <AppTopBarActionGroup>
          {selectedSummary && !selectedSummary.isActive ? (
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer gap-2"
              onClick={handleActivateMenu}
              disabled={!selectedMenuId || busy}
            >
              Use for booking
            </Button>
          ) : null}
          <Button
            size="sm"
            className="cursor-pointer gap-2"
            onClick={handleSaveMenu}
            disabled={!draft || busy || !isDirty}
          >
            Save
          </Button>
        </AppTopBarActionGroup>
      </AppTopBarActions>
      <AppWorkspaceTabbedPanel
        emptyLabel="No catalogs yet"
        tabs={(menusQuery.data ?? []).map((menu) => ({
          key: menu.id,
          active: menu.id === selectedMenuId,
          children: (
            <>
              <button
                type="button"
                className="flex h-8 min-w-0 flex-1 cursor-pointer items-center text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                onClick={() => setSelectedMenuId(menu.id)}
              >
                <span className="min-w-0 truncate font-medium">{menu.name}</span>
              </button>
              {menu.isActive ? (
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full border border-success/30 bg-success/70 shadow-[0_0_0_3px_color-mix(in_oklab,var(--success)_10%,transparent)]"
                  title="In use"
                >
                  <span className="sr-only">In use</span>
                </span>
              ) : null}
            </>
          ),
        }))}
        addControl={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Create catalog"
                className="flex h-[3.25rem] w-10 shrink-0 cursor-pointer items-center justify-center text-muted-foreground transition-colors hover:bg-muted/35 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                disabled={busy}
              >
                <Plus className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                New catalog
              </DropdownMenuLabel>
              <DropdownMenuItem className="cursor-pointer" onClick={handleCreateMenu}>
                <Plus className="h-4 w-4" />
                Blank catalog
              </DropdownMenuItem>
              {hasMenus ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Duplicate from
                  </DropdownMenuLabel>
                  {(menusQuery.data ?? []).map((menu) => (
                    <DropdownMenuItem
                      key={menu.id}
                      className="cursor-pointer"
                      onClick={() => handleDuplicateMenu(menu.id)}
                    >
                      <Copy className="h-4 w-4" />
                      <span className="truncate">{menu.name}</span>
                    </DropdownMenuItem>
                  ))}
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        }
      >
        <AppWorkspaceMainSupportingSplit>
          <AppWorkspacePane padded scroll>
            {!draft ? (
              !hasMenus ? (
                <div className="space-y-6">
                  <WorkspaceSectionCard className="p-0">
                    <WorkspaceEmptyState
                      badgeIcon={<Plus className="h-6 w-6 text-primary" />}
                      actionIcon={<Plus className="h-4 w-4" />}
                      title="Create your first offering catalog."
                      description="Add display groups, offerings, and pricing."
                      primaryActionLabel="Add catalog"
                      onPrimaryAction={handleCreateMenu}
                      isBusy={busy}
                      className="min-h-[18rem]"
                    />
                  </WorkspaceSectionCard>
                </div>
              ) : isSelectedMenuLoading ? (
                <WorkspaceNotice className="py-6">
                  <CardContent
                    className={`${surfaceSpacing.insetY.roomy} text-sm text-muted-foreground`}
                  >
                    Loading selected catalog…
                  </CardContent>
                </WorkspaceNotice>
              ) : selectedMenuError ? (
                <WorkspaceNotice tone="danger" className="py-6">
                  <CardContent className={`${surfaceSpacing.insetY.roomy} text-sm`}>
                    {selectedMenuError.message}
                  </CardContent>
                </WorkspaceNotice>
              ) : (
                <WorkspaceNotice className="py-6">
                  <CardContent
                    className={`${surfaceSpacing.insetY.roomy} text-sm text-muted-foreground`}
                  >
                    Select a catalog to start editing.
                  </CardContent>
                </WorkspaceNotice>
              )
            ) : (
              <div className="space-y-5">
                <CardContent className="space-y-5 p-0">
                  <WorkspaceSectionCard>
                    <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                          Catalog
                        </div>
                        <div className="mt-2 text-2xl font-semibold tracking-[-0.01em]">
                          {draft.name || 'Untitled catalog'}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">
                          {draftStats.itemCount}{' '}
                          {draftStats.itemCount === 1 ? 'offering' : 'offerings'}
                        </Badge>
                        <Badge variant="outline">{draftStats.categoryCount} groups</Badge>
                      </div>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="menu-name">Catalog label</Label>
                        <Input
                          id="menu-name"
                          value={draft.name}
                          placeholder="Untitled"
                          onChange={(event) => {
                            const nextName = event.target.value
                            updateDraft({
                              name: nextName,
                              slug: draft.id ? draft.slug : slugify(nextName),
                            })
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Catalog slug</Label>
                        <Input
                          value={draft.slug}
                          onChange={(event) => updateDraft({ slug: slugify(event.target.value) })}
                        />
                      </div>
                    </div>
                  </WorkspaceSectionCard>

                  <WorkspaceSectionCard className="space-y-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <div className="text-sm font-medium text-foreground">Offering catalog</div>
                      </div>
                      <Button variant="outline" className="cursor-pointer" onClick={addCategory}>
                        <Plus className="h-4 w-4" />
                        Add display group
                      </Button>
                    </div>
                    {draft.categories.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No display groups yet. Add a display group to start building offerings.
                      </p>
                    ) : (
                      draft.categories.map((category, categoryIndex) => (
                        <div
                          key={category.id ?? `category-${categoryIndex}`}
                          className="overflow-hidden rounded-[6px] border border-border/70 bg-background"
                        >
                          <div className="border-b border-border/70 bg-muted/[0.16] p-4">
                            <div className="grid gap-3 xl:grid-cols-[minmax(16rem,1fr)_14rem_7rem_15rem] xl:items-end">
                              <div className="min-w-0 space-y-1.5">
                                <Label
                                  htmlFor={`display-group-${categoryIndex}`}
                                  className="text-xs font-semibold text-muted-foreground"
                                >
                                  Display group
                                </Label>
                                <Input
                                  id={`display-group-${categoryIndex}`}
                                  value={category.name}
                                  onChange={(event) =>
                                    updateCategory(categoryIndex, { name: event.target.value })
                                  }
                                  placeholder="Display group name"
                                  className="h-10 bg-background"
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label
                                  htmlFor={`group-pricing-${categoryIndex}`}
                                  className="text-xs font-semibold text-muted-foreground"
                                >
                                  Default pricing
                                </Label>
                                <PricingDisplaySelect
                                  id={`group-pricing-${categoryIndex}`}
                                  value={category.bookingPageConfig.priceDisplay}
                                  onChange={(value) => {
                                    if (value === 'inherit') return
                                    updateCategory(categoryIndex, {
                                      bookingPageConfig: {
                                        ...category.bookingPageConfig,
                                        priceDisplay: value,
                                      },
                                    })
                                  }}
                                />
                              </div>
                              <div className="space-y-1.5">
                                <div className="text-xs font-semibold text-muted-foreground">
                                  Status
                                </div>
                                <div className="flex h-10 min-w-0 items-center justify-center gap-2 rounded-[6px] border border-border/70 bg-background px-3 text-sm font-medium">
                                  <Switch
                                    aria-label="Set category active"
                                    checked={category.isActive}
                                    onCheckedChange={(checked) =>
                                      updateCategory(categoryIndex, { isActive: checked })
                                    }
                                  />
                                  Active
                                </div>
                              </div>
                              <div className="space-y-1.5">
                                <div className="text-xs font-semibold text-muted-foreground">
                                  Actions
                                </div>
                                <div className="grid grid-cols-[minmax(0,1fr)_2.5rem_2.5rem_2.5rem] items-center gap-2">
                                  <Button
                                    variant="outline"
                                    className="h-10 w-full cursor-pointer justify-center px-3"
                                    onClick={() => addItem(categoryIndex)}
                                  >
                                    <Plus className="h-4 w-4" />
                                    Add
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-10 w-10 cursor-pointer justify-self-center"
                                    onClick={() => moveCategory(categoryIndex, 'up')}
                                    disabled={categoryIndex === 0}
                                    aria-label="Move display group up"
                                  >
                                    <ChevronUp className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-10 w-10 cursor-pointer justify-self-center"
                                    onClick={() => moveCategory(categoryIndex, 'down')}
                                    disabled={categoryIndex === draft.categories.length - 1}
                                    aria-label="Move display group down"
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-10 w-10 cursor-pointer justify-self-center"
                                    onClick={() => removeCategory(categoryIndex)}
                                    aria-label="Delete display group"
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                          {category.items.length === 0 ? (
                            <p className="p-4 text-sm text-muted-foreground">
                              No offerings yet. Add your first offering in this display group.
                            </p>
                          ) : (
                            <div className="divide-y divide-border/70">
                              <div className="hidden grid-cols-[2.25rem_minmax(0,1fr)_7rem_7rem_10rem] items-center gap-3 bg-muted/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground lg:grid">
                                <span className="sr-only">Active</span>
                                <span>Offering</span>
                                <span>Minutes</span>
                                <span>Price</span>
                                <span className="text-right">Actions</span>
                              </div>
                              {category.items.map((item, itemIndex) => {
                                const rowOpen = expandedOfferingKeys.has(
                                  getOfferingKey(categoryIndex, itemIndex)
                                )

                                return (
                                  <div
                                    key={item.id ?? `item-${itemIndex}`}
                                    className={cn(
                                      'space-y-3 p-3 transition-colors',
                                      rowOpen ? 'bg-primary/[0.025]' : 'hover:bg-muted/15'
                                    )}
                                  >
                                    <div
                                      className="grid cursor-pointer gap-3 lg:grid-cols-[2.25rem_minmax(0,1fr)_7rem_7rem_10rem] lg:items-center"
                                      onPointerDown={(event) => {
                                        const target = event.target as HTMLElement
                                        if (target.closest('button,input,label,textarea')) return
                                        toggleOfferingRow(categoryIndex, itemIndex)
                                      }}
                                    >
                                      <label
                                        className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-[4px] border border-border/70 bg-background"
                                        title={item.isActive ? 'Active' : 'Hidden'}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={item.isActive}
                                          onChange={(event) =>
                                            updateItem(categoryIndex, itemIndex, {
                                              isActive: event.target.checked,
                                            })
                                          }
                                          aria-label={
                                            item.isActive ? 'Active offering' : 'Hidden offering'
                                          }
                                          className="h-4 w-4 accent-primary"
                                        />
                                      </label>
                                      <Input
                                        value={item.name}
                                        className="h-10"
                                        onFocus={() => expandOfferingRow(categoryIndex, itemIndex)}
                                        onChange={(event) => {
                                          const nextName = event.target.value
                                          updateItem(categoryIndex, itemIndex, {
                                            name: nextName,
                                            slug: item.id ? item.slug : slugify(nextName),
                                          })
                                        }}
                                        placeholder="Standard Visit"
                                      />
                                      <Input
                                        type="number"
                                        min={5}
                                        value={item.durationMinutes}
                                        aria-label="Duration in minutes"
                                        className="h-10"
                                        onFocus={() => expandOfferingRow(categoryIndex, itemIndex)}
                                        onChange={(event) =>
                                          updateItem(categoryIndex, itemIndex, {
                                            durationMinutes: Number(event.target.value) || 5,
                                          })
                                        }
                                      />
                                      <Input
                                        value={formatCents(item.price)}
                                        placeholder="125.00"
                                        aria-label="Price"
                                        className="h-10"
                                        onFocus={() => expandOfferingRow(categoryIndex, itemIndex)}
                                        onChange={(event) =>
                                          updateItem(categoryIndex, itemIndex, {
                                            price: parseCurrencyToCents(event.target.value),
                                          })
                                        }
                                      />
                                      <div className="flex items-center justify-start gap-1 lg:justify-end">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className={cn(
                                            'h-8 cursor-pointer px-2 text-xs font-medium text-muted-foreground',
                                            rowOpen ? 'text-primary hover:text-primary' : ''
                                          )}
                                          onClick={() =>
                                            toggleOfferingRow(categoryIndex, itemIndex)
                                          }
                                        >
                                          {rowOpen ? 'Details' : 'Edit'}
                                          <ChevronDown
                                            className={cn(
                                              'h-3.5 w-3.5 transition-transform',
                                              rowOpen ? 'rotate-180' : ''
                                            )}
                                          />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 cursor-pointer"
                                          onClick={() => moveItem(categoryIndex, itemIndex, 'up')}
                                          disabled={itemIndex === 0}
                                          aria-label="Move offering up"
                                        >
                                          <ChevronUp className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 cursor-pointer"
                                          onClick={() => moveItem(categoryIndex, itemIndex, 'down')}
                                          disabled={itemIndex === category.items.length - 1}
                                          aria-label="Move offering down"
                                        >
                                          <ChevronDown className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>

                                    {rowOpen ? (
                                      <div className="grid gap-3 border-t border-border/60 pt-3 xl:grid-cols-[minmax(0,1fr)_minmax(20rem,0.78fr)]">
                                        <section className="space-y-3 rounded-[6px] border border-border/70 bg-background/80 p-3">
                                          <div>
                                            <div className="text-sm font-semibold">
                                              Public booking details
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                              Customer-facing copy and display treatment.
                                            </div>
                                          </div>
                                          <div className="space-y-2">
                                            <Label>Description</Label>
                                            <Textarea
                                              rows={2}
                                              value={item.description}
                                              onChange={(event) =>
                                                updateItem(categoryIndex, itemIndex, {
                                                  description: event.target.value,
                                                })
                                              }
                                              placeholder="What customers should know before booking."
                                            />
                                          </div>
                                          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem]">
                                            <BadgeLabelEditor
                                              id={`booking-badge-${categoryIndex}-${itemIndex}`}
                                              value={item.bookingPageConfig.badgeLabel}
                                              onChange={(value) =>
                                                updateItem(categoryIndex, itemIndex, {
                                                  bookingPageConfig: {
                                                    ...item.bookingPageConfig,
                                                    badgeLabel: value,
                                                  },
                                                })
                                              }
                                            />
                                            <div className="space-y-2">
                                              <Label
                                                htmlFor={`pricing-display-${categoryIndex}-${itemIndex}`}
                                              >
                                                Pricing display
                                              </Label>
                                              <PricingDisplaySelect
                                                id={`pricing-display-${categoryIndex}-${itemIndex}`}
                                                value={item.bookingPageConfig.priceDisplay}
                                                includeInherit
                                                onChange={(value) =>
                                                  updateItem(categoryIndex, itemIndex, {
                                                    bookingPageConfig: {
                                                      ...item.bookingPageConfig,
                                                      priceDisplay: value,
                                                    },
                                                  })
                                                }
                                              />
                                            </div>
                                          </div>
                                          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem]">
                                            <div className="space-y-2 rounded-[6px] border border-border/70 bg-muted/15 p-3">
                                              <div className="flex items-center justify-between gap-3">
                                                <Label
                                                  htmlFor={`service-image-${categoryIndex}-${itemIndex}`}
                                                >
                                                  Service image
                                                </Label>
                                                {pendingServiceImages[
                                                  `${categoryIndex}-${itemIndex}`
                                                ] ? (
                                                  <span className="text-xs text-muted-foreground">
                                                    Selected locally
                                                  </span>
                                                ) : (
                                                  <Image className="size-4 text-muted-foreground" />
                                                )}
                                              </div>
                                              {item.bookingPageConfig.imageUrl ? (
                                                <img
                                                  src={item.bookingPageConfig.imageUrl}
                                                  alt=""
                                                  className="aspect-[16/9] w-full rounded-[6px] object-cover"
                                                />
                                              ) : null}
                                              <Input
                                                id={`service-image-${categoryIndex}-${itemIndex}`}
                                                value={item.bookingPageConfig.imageUrl}
                                                onChange={(event) =>
                                                  updateItem(categoryIndex, itemIndex, {
                                                    bookingPageConfig: {
                                                      ...item.bookingPageConfig,
                                                      imageUrl: event.target.value,
                                                    },
                                                  })
                                                }
                                                placeholder="/api/storage/service-images/item.webp"
                                              />
                                              <Input
                                                type="file"
                                                accept="image/*"
                                                onChange={(event) => {
                                                  previewServiceImage(
                                                    categoryIndex,
                                                    itemIndex,
                                                    event.target.files?.[0]
                                                  )
                                                  event.target.value = ''
                                                }}
                                              />
                                            </div>
                                            <div className="space-y-2">
                                              <Label
                                                htmlFor={`featured-label-${categoryIndex}-${itemIndex}`}
                                              >
                                                Featured label
                                              </Label>
                                              <Input
                                                id={`featured-label-${categoryIndex}-${itemIndex}`}
                                                value={item.bookingPageConfig.featuredLabel}
                                                maxLength={32}
                                                onChange={(event) =>
                                                  updateItem(categoryIndex, itemIndex, {
                                                    bookingPageConfig: {
                                                      ...item.bookingPageConfig,
                                                      featuredLabel: event.target.value,
                                                    },
                                                  })
                                                }
                                                placeholder="Best value"
                                              />
                                              <Label
                                                htmlFor={`upsell-note-${categoryIndex}-${itemIndex}`}
                                              >
                                                Upsell note
                                              </Label>
                                              <Textarea
                                                id={`upsell-note-${categoryIndex}-${itemIndex}`}
                                                rows={3}
                                                value={item.bookingPageConfig.upsellNote}
                                                maxLength={160}
                                                onChange={(event) =>
                                                  updateItem(categoryIndex, itemIndex, {
                                                    bookingPageConfig: {
                                                      ...item.bookingPageConfig,
                                                      upsellNote: event.target.value,
                                                    },
                                                  })
                                                }
                                                placeholder="Optional add-on or upgrade note."
                                              />
                                            </div>
                                          </div>
                                          <PrepChecklistEditor
                                            id={`included-items-${categoryIndex}-${itemIndex}`}
                                            label="Included items"
                                            emptyLabel="No included items yet."
                                            placeholder="Add an included item"
                                            items={item.bookingPageConfig.includedItems}
                                            onChange={(includedItems) =>
                                              updateItem(categoryIndex, itemIndex, {
                                                bookingPageConfig: {
                                                  ...item.bookingPageConfig,
                                                  includedItems,
                                                },
                                              })
                                            }
                                          />
                                          <PrepChecklistEditor
                                            id={`prep-checklist-${categoryIndex}-${itemIndex}`}
                                            items={item.bookingPageConfig.prepChecklist}
                                            onChange={(prepChecklist) =>
                                              updateItem(categoryIndex, itemIndex, {
                                                bookingPageConfig: {
                                                  ...item.bookingPageConfig,
                                                  prepChecklist,
                                                },
                                              })
                                            }
                                          />
                                        </section>
                                        <section className="space-y-3 rounded-[6px] border border-border/70 bg-background/80 p-3">
                                          <div>
                                            <div className="text-sm font-semibold">
                                              Scheduling controls
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                              URL, notice, buffers, and quantity behavior.
                                            </div>
                                          </div>
                                          <div className="grid gap-3 md:grid-cols-2">
                                            <div className="space-y-2 md:col-span-2">
                                              <Label>Booking URL label</Label>
                                              <Input
                                                value={item.slug}
                                                onChange={(event) =>
                                                  updateItem(categoryIndex, itemIndex, {
                                                    slug: slugify(event.target.value),
                                                  })
                                                }
                                              />
                                            </div>
                                            <div className="space-y-2">
                                              <Label>Lead time</Label>
                                              <Input
                                                type="number"
                                                min={0}
                                                value={item.minimumNoticeMinutes}
                                                onChange={(event) =>
                                                  updateItem(categoryIndex, itemIndex, {
                                                    minimumNoticeMinutes:
                                                      Number(event.target.value) || 0,
                                                  })
                                                }
                                              />
                                            </div>
                                            <div className="space-y-2">
                                              <Label>Travel buffer before</Label>
                                              <Input
                                                type="number"
                                                min={0}
                                                value={item.bufferMinutesBefore}
                                                onChange={(event) =>
                                                  updateItem(categoryIndex, itemIndex, {
                                                    bufferMode: 'custom',
                                                    bufferMinutesBefore:
                                                      Number(event.target.value) || 0,
                                                  })
                                                }
                                              />
                                            </div>
                                            <div className="space-y-2">
                                              <Label>Travel buffer after</Label>
                                              <Input
                                                type="number"
                                                min={0}
                                                value={item.bufferMinutesAfter}
                                                onChange={(event) =>
                                                  updateItem(categoryIndex, itemIndex, {
                                                    bufferMode: 'custom',
                                                    bufferMinutesAfter:
                                                      Number(event.target.value) || 0,
                                                  })
                                                }
                                              />
                                            </div>
                                          </div>
                                          <div className="flex h-10 items-center justify-between gap-3 rounded-[4px] border border-border/70 bg-muted/20 px-3">
                                            <span className="text-sm font-medium">
                                              Let customers choose a quantity
                                            </span>
                                            <Switch
                                              checked={item.allowQuantity}
                                              onCheckedChange={(checked) =>
                                                updateItem(categoryIndex, itemIndex, {
                                                  allowQuantity: checked,
                                                })
                                              }
                                            />
                                          </div>
                                          {item.allowQuantity ? (
                                            <div className="grid gap-3 border-l-2 border-primary/20 pl-3 md:grid-cols-2">
                                              <div className="space-y-2">
                                                <Label>Quantity name</Label>
                                                <Input
                                                  value={item.quantityLabel}
                                                  onChange={(event) =>
                                                    updateItem(categoryIndex, itemIndex, {
                                                      quantityLabel: event.target.value,
                                                    })
                                                  }
                                                />
                                              </div>
                                              <div className="space-y-2">
                                                <Label>Maximum</Label>
                                                <Input
                                                  type="number"
                                                  min={1}
                                                  value={item.maxQuantity}
                                                  onChange={(event) =>
                                                    updateItem(categoryIndex, itemIndex, {
                                                      maxQuantity: Number(event.target.value) || 1,
                                                    })
                                                  }
                                                />
                                              </div>
                                              <div className="space-y-2">
                                                <Label>Each additional unit</Label>
                                                <Input
                                                  value={formatCents(item.pricePerAdditionalUnit)}
                                                  onChange={(event) =>
                                                    updateItem(categoryIndex, itemIndex, {
                                                      pricePerAdditionalUnit:
                                                        parseCurrencyToCents(event.target.value) ??
                                                        0,
                                                    })
                                                  }
                                                />
                                              </div>
                                              <div className="space-y-2">
                                                <Label>Extra time per unit</Label>
                                                <Input
                                                  type="number"
                                                  min={0}
                                                  value={item.durationPerAdditionalUnit}
                                                  onChange={(event) =>
                                                    updateItem(categoryIndex, itemIndex, {
                                                      durationPerAdditionalUnit:
                                                        Number(event.target.value) || 0,
                                                    })
                                                  }
                                                />
                                              </div>
                                            </div>
                                          ) : null}
                                          <div className="flex justify-end">
                                            <Button
                                              variant="ghost"
                                              className="cursor-pointer gap-2 text-destructive hover:text-destructive"
                                              onClick={() => removeItem(categoryIndex, itemIndex)}
                                            >
                                              <Trash2 className="h-4 w-4" />
                                              Delete offering
                                            </Button>
                                          </div>
                                        </section>
                                      </div>
                                    ) : null}
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </WorkspaceSectionCard>
                </CardContent>
              </div>
            )}
          </AppWorkspacePane>
          <WorkspaceInspectorPane>
            <section className="space-y-4">
              <div className="app-section-label">Details</div>
              {draft ? (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold">Catalog</div>
                    {selectedSummary?.isActive ? (
                      <Badge variant="outline" className="border-primary/40 bg-primary/10">
                        In use
                      </Badge>
                    ) : (
                      <Badge variant="outline">Not in use</Badge>
                    )}
                  </div>
                  <WorkspaceMetricStrip
                    className="grid-cols-3"
                    metrics={[
                      { key: 'offerings', label: 'offerings', value: draftStats.itemCount },
                      { key: 'active', label: 'active', value: draftStats.activeItemCount },
                      {
                        key: 'avg',
                        label: 'avg min',
                        value: draftStats.averageDuration || '-',
                      },
                    ]}
                  />
                  <div className="space-y-2">
                    <WorkspaceStatusRow
                      label="Booking page"
                      value={selectedSummary?.isActive ? 'Default catalog' : 'Draft catalog'}
                    />
                    <WorkspaceStatusRow label="Pricing" value="USD" />
                  </div>
                  <div className="space-y-3 border-t border-border/70 pt-4">
                    <div className="grid gap-3">
                      <div className="space-y-2">
                        <Label>Default buffer before</Label>
                        <Input
                          type="number"
                          min={0}
                          value={draft.bufferMinutesBeforeDefault}
                          onChange={(event) =>
                            updateDraft({
                              bufferMinutesBeforeDefault: Number(event.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Default buffer after</Label>
                        <Input
                          type="number"
                          min={0}
                          value={draft.bufferMinutesAfterDefault}
                          onChange={(event) =>
                            updateDraft({
                              bufferMinutesAfterDefault: Number(event.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Minimum notice</Label>
                        <Input
                          type="number"
                          min={0}
                          value={draft.minimumNoticeMinutesDefault}
                          onChange={(event) =>
                            updateDraft({
                              minimumNoticeMinutesDefault: Number(event.target.value) || 0,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 border-t border-border/70 pt-4">
                    <Button
                      variant="ghost"
                      className="w-full cursor-pointer justify-start gap-2 text-destructive hover:text-destructive"
                      onClick={handleDeleteMenu}
                      disabled={selectedSummary?.isActive || !selectedMenuId || busy}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete catalog
                    </Button>
                  </div>
                </>
              ) : null}
            </section>

            <section className="space-y-3 border-t border-border/70 pt-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Booking preview
              </div>
              {previewCategoryGroups.length === 0 ? (
                <WorkspaceNotice className="border-dashed p-4">
                  Add a display group and offerings to see the preview.
                </WorkspaceNotice>
              ) : (
                previewCategoryGroups.slice(0, 3).map((category) => (
                  <div
                    key={category.name}
                    className="rounded-[6px] border border-border/70 bg-background/85 p-3"
                  >
                    <div className="mb-2 text-sm font-semibold">{category.name}</div>
                    <div className="space-y-2">
                      {category.items.length === 0 ? (
                        <div className="rounded-[4px] border border-dashed border-border/70 px-3 py-2 text-xs text-muted-foreground">
                          No offerings yet.
                        </div>
                      ) : (
                        category.items.slice(0, 3).map((item) => (
                          <div
                            key={`${category.name}-${item.name}`}
                            className="rounded-[4px] border border-border/70 bg-background px-3 py-2"
                          >
                            <div className="flex items-center justify-between gap-2 text-sm">
                              <span className="min-w-0 truncate">{item.name}</span>
                              {item.price ? (
                                <span className="shrink-0 text-muted-foreground">{item.price}</span>
                              ) : null}
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              {item.duration}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))
              )}
            </section>
          </WorkspaceInspectorPane>
        </AppWorkspaceMainSupportingSplit>
      </AppWorkspaceTabbedPanel>
      {saveMutation.error ? (
        <p className="text-sm text-destructive">{saveMutation.error.message}</p>
      ) : null}
      {activateMutation.error ? (
        <p className="text-sm text-destructive">{activateMutation.error.message}</p>
      ) : null}
      {deleteMutation.error ? (
        <p className="text-sm text-destructive">{deleteMutation.error.message}</p>
      ) : null}
    </div>
  )
}
